import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, LayoutTemplate, Megaphone, MessageSquareText, Sparkles, TrendingUp, Workflow } from 'lucide-react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { AI_CATEGORIES, AI_TOOLS, PLAN_LEVEL, PLANS } from '../../plans';
import { aiToolGuide } from '../../workspaceGuide';
import { Badge, Button, EmptyState, FilterChips, PageHeader, SearchInput, SectionHeader, Skeleton } from '../../components/ui';
import { failedFindings } from './insights';
import './dashboard.css';

const CATEGORY_ICON = { content: LayoutTemplate, marketing: Megaphone, customer: MessageSquareText, automation: Workflow, revenue: TrendingUp };

function ToolCard({ tool, locked }) {
  const Icon = CATEGORY_ICON[tool.category] || Sparkles;
  const category = AI_CATEGORIES.find(item => item.id === tool.category);
  return (
    <article className={`studio-card${locked ? ' is-locked' : ''}`}>
      <div className="studio-card-head">
        <span className="studio-card-icon"><Icon size={17} aria-hidden="true" /></span>
        {locked ? <Badge tone="info" className="is-plan">{PLANS[tool.minimum].name}</Badge> : <Badge tone="neutral">{category?.label}</Badge>}
      </div>
      <h3>{tool.name}</h3>
      <p>{tool.description}</p>
      <div className="studio-card-foot">
        {locked
          ? <Link className="dt-btn is-link" to="/#pricing">Upgrade to unlock <ArrowRight size={14} /></Link>
          : <Link className="dt-btn is-link" to={`/dashboard/ai-studio/${tool.id}`}>Open tool <ArrowRight size={14} /></Link>}
      </div>
    </article>
  );
}

export default function AIStudioPage() {
  const { workspace, loading } = useWorkspace();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');

  const level = PLAN_LEVEL[workspace.plan] || 1;
  const isLocked = tool => level < PLAN_LEVEL[tool.minimum];

  const recentTools = useMemo(() => {
    const seen = new Set();
    return (workspace.generations || [])
      .map(item => item.tool_key)
      .filter(key => { if (seen.has(key)) return false; seen.add(key); return true; })
      .map(key => AI_TOOLS.find(tool => tool.id === key))
      .filter(Boolean)
      .slice(0, 3);
  }, [workspace.generations]);

  const recommended = useMemo(() => {
    const ids = [];
    const scan = (workspace.scans || [])[0];
    if (!scan || failedFindings(scan).length) ids.push('seo_page_writer');
    if ((workspace.prospects || []).some(lead => lead.status === 'new')) ids.push('followup_writer');
    if (!(workspace.generations || []).length) ids.push('website_builder');
    if ((workspace.keywords || []).some(item => Number(item.maps_rank || item.search_rank) > 10)) ids.push('seo_page_writer');
    return [...new Set(ids)]
      .map(id => AI_TOOLS.find(tool => tool.id === id))
      .filter(tool => tool && !isLocked(tool))
      .slice(0, 3);
  }, [workspace]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return AI_TOOLS.filter(tool => {
      const matchesCategory = category === 'all' || tool.category === category;
      if (!matchesCategory) return false;
      if (!term) return true;
      return `${tool.name} ${tool.description} ${aiToolGuide(tool.id).useCase}`.toLowerCase().includes(term);
    });
  }, [query, category]);

  const filters = [
    { id: 'all', label: 'All', count: AI_TOOLS.length },
    ...AI_CATEGORIES.map(item => ({ id: item.id, label: item.label, count: AI_TOOLS.filter(tool => tool.category === item.id).length })),
  ];

  if (loading) return <div className="dt-stack-4"><Skeleton height={70} /><Skeleton height={44} /><div className="studio-grid">{Array.from({ length: 6 }, (_, index) => <Skeleton key={index} height={150} />)}</div></div>;

  return (
    <div className="dt-stack-5">
      <PageHeader title="AI Studio" description="Create, automate, and grow your business using Data Tallied AI." />

      <div className="studio-toolbar">
        <SearchInput value={query} onChange={event => setQuery(event.target.value)} placeholder="Search tools..." label="Search AI tools" />
        <FilterChips options={filters} value={category} onChange={setCategory} label="Filter tools by category" />
      </div>

      {!query && category === 'all' && recentTools.length > 0 && (
        <section className="studio-group" aria-label="Recently used">
          <SectionHeader title="Recently used" />
          <div className="studio-grid">{recentTools.map(tool => <ToolCard key={tool.id} tool={tool} locked={isLocked(tool)} />)}</div>
        </section>
      )}

      {!query && category === 'all' && recommended.length > 0 && (
        <section className="studio-group" aria-label="Recommended for you">
          <SectionHeader title="Recommended for you" description="Based on your scans, leads, and rankings." />
          <div className="studio-grid">{recommended.map(tool => <ToolCard key={tool.id} tool={tool} locked={false} />)}</div>
        </section>
      )}

      {!filtered.length ? (
        <div className="dt-card">
          <EmptyState icon={Sparkles} title="No tools match your search" description="Try a different term or clear the category filter." actions={<Button variant="secondary" onClick={() => { setQuery(''); setCategory('all'); }}>Clear filters</Button>} />
        </div>
      ) : (
        AI_CATEGORIES.filter(item => filtered.some(tool => tool.category === item.id)).map(item => (
          <section className="studio-group" key={item.id} aria-label={item.label}>
            <SectionHeader title={item.label} />
            <div className="studio-grid">
              {filtered.filter(tool => tool.category === item.id).map(tool => <ToolCard key={tool.id} tool={tool} locked={isLocked(tool)} />)}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
